// frontend/src/components/Chats.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardMedia, CardContent, CardActionArea, Fab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BsPeople } from 'react-icons/bs'; // Иконка группы
import { AiOutlinePlus } from 'react-icons/ai'; // Иконка плюсика
import { keyframes } from '@mui/system';
import { getCurrentIdentity } from '../userIdentity';

// Создаем анимацию "парения" (вверх-вниз)
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Chats = () => {
  const [chats, setChats] = useState([]);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const loadIdentity = async () => {
      const identity = await getCurrentIdentity();

      if (!mounted) return;

      setUserId(identity.userId);
    };

    loadIdentity();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) return;

    fetchChats();

    const timer = setInterval(fetchChats, 4000);

    return () => clearInterval(timer);
  }, [userId]);

  const fetchChats = async () => {
    try {
      const response = await fetch(`/api/chats/${userId}`);

      if (!response.ok) return;

      const data = await response.json();
      setChats(data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    }
  };

  const handleChatClick = (chatId) => {
    navigate(`/chat/${chatId}`);
  };

  const handleAvatarClick = (chat, e) => {
    e.stopPropagation();
    if (chat.isGroup) {
      // Если это группа, логично, что по клику на аватар мы не должны идти в профиль бота.
      // Отправляем пользователя просто в чат группы.
      navigate(`/chat/${chat.id}`);
    } else {
      navigate(`/bot/${chat.botId}`);
    }
  };

  return (
    <Box sx={{ p: 1, height: '100%', position: 'relative' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center', fontStyle: 'italic', color: '#fff' }}>
        Чаты
      </Typography>

      <Grid container spacing={1} sx={{ pb: '120px' }}> {/* Увеличили отступ снизу для кнопок */}
        {chats.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', width: '100%', mt: 4 }}>
            У вас пока нет чатов, напишите кому-нибудь
          </Typography>
        ) : (
          chats.map((chat) => {
            // Определяем, что показывать в зависимости от того, группа это или обычный бот
            const displayName = chat.isGroup && chat.groupName ? chat.groupName : chat.botName;
            const displayAvatar = chat.isGroup && chat.groupAvatar ? chat.groupAvatar : chat.botAvatar;

            return (
              <Grid item xs={12} key={chat.id}>
                <Card 
                  sx={{ 
                    borderRadius: 0, 
                    backgroundColor: 'transparent', 
                    boxShadow: 'none',
                    borderBottom: '1px solid #333',
                    '&:hover': { backgroundColor: '#2a2a2a' } 
                  }}
                >
                  <CardActionArea 
                      onClick={() => handleChatClick(chat.id)}
                      sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', p: 1 }}
                  >
                      <CardMedia 
                          component="img" 
                          image={displayAvatar || 'https://via.placeholder.com/60'} 
                          alt={displayName} 
                          sx={{ 
                              width: 50, 
                              height: 50, 
                              borderRadius: '50%', 
                              mr: 2, 
                              zIndex: 2,
                              border: '1px solid #444',
                              objectFit: 'cover' // Поможет аватаркам не сплющиваться
                          }} 
                          // Передаем весь объект chat вместо chat.botId
                          onClick={(e) => handleAvatarClick(chat, e)} 
                      />
                      <CardContent sx={{ flex: 1, p: '0 !important', overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff', fontSize: '1rem' }}>
                                  {displayName}
                              </Typography>
                          </Box>
                          <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                  color: '#aaa',
                                  overflow: 'hidden', 
                                  whiteSpace: 'nowrap', 
                                  textOverflow: 'ellipsis',
                                  fontSize: '0.9rem'
                              }}
                          >
                              {chat.lastMessage ? chat.lastMessage.slice(0, 50) + (chat.lastMessage.length > 50 ? '...' : '') : 'Нет сообщений'}
                          </Typography>
                      </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* --- ПАРЯЩИЕ КНОПКИ --- */}
      
      {/* Кнопка Группы (Слева) */}
      <Fab 
        onClick={() => navigate('/groups')}
        sx={{ 
          position: 'fixed', 
          bottom: '90px', 
          left: '20px', 
          backgroundColor: '#1e1e1e', 
          color: '#007AFF',
          border: '1px solid #333',
          animation: `${float} 3s ease-in-out infinite`,
          '&:hover': { backgroundColor: '#2a2a2a' }
        }}
      >
        <BsPeople size={24} />
      </Fab>

      {/* Кнопка Добавить/Боты (Справа) */}
      <Fab 
        onClick={() => navigate('/bots')}
        sx={{ 
          position: 'fixed', 
          bottom: '90px', 
          right: '20px', 
          backgroundColor: '#007AFF', 
          color: '#fff',
          animation: `${float} 3s ease-in-out infinite`,
          animationDelay: '1.5s', // Задержка, чтобы они парили вразнобой
          '&:hover': { backgroundColor: '#005BB5' }
        }}
      >
        <AiOutlinePlus size={24} />
      </Fab>
    </Box>
  );
};

export default Chats;